import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { crudeApi, crudeAxios } from '../../../helpers/crude_api_helper';

// Backend entity: CrudeDocumentUpload — Crude / Master Data.
// See api-contract-crude-document-upload.md. Unlike its siblings (CDU,
// EvaluationDocument, ReceptionFacility) this entity does NOT go through the
// ChangeRequest review/approval workflow — create/update/delete take effect
// immediately, so there is no `dataStatus` field and no locked-row handling.
// Both write endpoints bind with [FromForm] AND an IFormFile → send
// multipart/form-data (postMultipart/putMultipart), not urlencoded and not JSON.
// PUT has no {id} route param — the id goes in the form body.
const DOC_ENDPOINT = '/v1/CrudeDocumentUpload';
const DOC_LIST_ENDPOINT = '/v1/CrudeDocumentUpload/GetListCrudeDocumentUpload';
// Preview/download the stored file (added BE-side 2026-07-28). Streams the raw
// binary (Content-Type = stored fileContentType); same endpoint for inline
// preview and download — only the FE handling differs. Uses the row's own id.
const DOC_DOWNLOAD_ENDPOINT = id => `/v1/CrudeDocumentUpload/Download/${id}`;

// Server-side sort whitelist (PascalCase property names — wrong casing → 400).
const SORT_FIELDS = {
  documentName: 'DocumentName',
  description: 'Description',
  documentType: 'DocumentType',
};

// The API serialises this enum as a string, so the value IS the wire format.
const documentTypeOptions = [
  { value: 'Procurement', label: 'Procurement' },
  { value: 'HPS', label: 'HPS' },
];

const formatFileSize = bytes => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const emptyForm = {
  id: null,
  documentName: '',
  description: '',
  documentType: 'Procurement',
  fileName: '',
};

const DocumentUploadView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // File preview modal. `previewUrl` is an object URL that must be revoked when
  // the modal closes / the component unmounts to avoid leaking blob memory.
  const [previewModal, setPreviewModal] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  // Server-side pagination / search / sort state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');

  document.title = 'Document Upload';

  // Debounce the search box, then reset to page 1 for a fresh server query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // `searchText` only covers documentName + description server-side; the
      // Document Type filter is a separate param (the two are AND-ed).
      const result = await crudeApi.get(DOC_LIST_ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
        documentType:
          documentTypeFilter === 'all' ? undefined : documentTypeFilter,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortField,
    sortOrder,
    documentTypeFilter,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleFileChange = e => {
    setSelectedFile(e.target.files[0] || null);
    setErrors({ ...errors, file: '' });
  };

  // No duplicate-name check here — the API deliberately allows duplicates and
  // only validates mandatory fields.
  const validateForm = () => {
    const newErrors = {};
    if (!formData.documentName || !formData.documentName.trim()) {
      newErrors.documentName = 'Document Name is required';
    } else if (formData.documentName.trim().length > 250) {
      newErrors.documentName = 'Document Name max 250 characters';
    }
    if (!formData.description || !formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.documentType) {
      newErrors.documentType = 'Document Type is required';
    }
    // File is mandatory on Create only; on Update an empty input keeps the
    // existing file.
    if (!formData.id && !selectedFile) {
      newErrors.file = 'File is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setSelectedFile(null);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    setFormData({
      id: row.id,
      documentName: row.documentName || '',
      description: row.description || '',
      documentType: row.documentType || 'Procurement',
      fileName: row.fileName || '',
    });
    setSelectedFile(null);
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);

    const payload = {
      documentName: formData.documentName.trim(),
      description: formData.description.trim(),
      documentType: formData.documentType,
    };

    try {
      if (formData.id) {
        // Update → PUT multipart (id in body, no route param). `file` is omitted
        // when the user did not pick a new one, which keeps the stored file.
        await crudeApi.putMultipart(DOC_ENDPOINT, {
          id: formData.id,
          ...payload,
          file: selectedFile || undefined,
        });
        toast.success('Document has been updated');
      } else {
        await crudeApi.postMultipart(DOC_ENDPOINT, {
          ...payload,
          file: selectedFile,
        });
        toast.success('Document has been added');
      }
      setModalForm(false);
      fetchData();
    } catch (error) {
      toast.error(
        `Failed to ${formData.id ? 'update' : 'save'} data: ${error}`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      // Soft-delete; the row drops off the list immediately (no approval step).
      await crudeApi.delete(`${DOC_ENDPOINT}/${selectedRow.id}`);
      toast.success('Document has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // Fetch the file as a blob and open it in the preview modal. The same endpoint
  // serves preview and download — PDFs/images render inline, anything else falls
  // back to a download prompt inside the modal.
  const handleView = async row => {
    setPreviewLoadingId(row.id);
    try {
      const blob = await crudeAxios.get(DOC_DOWNLOAD_ENDPOINT(row.id), {
        responseType: 'blob',
      });
      const type =
        blob?.type || row.fileContentType || 'application/octet-stream';
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewRow({ ...row, fileContentType: type });
      setPreviewModal(true);
    } catch (error) {
      toast.error(`Failed to open file: ${error}`);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const closePreview = () => {
    setPreviewModal(false);
    setPreviewRow(null);
    setPreviewUrl('');
  };

  const handleDownloadPreview = () => {
    if (!previewUrl || !previewRow) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = previewRow.fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Revoke the object URL whenever it changes or on unmount to avoid leaking it.
  useEffect(
    () => () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  // Server-side sort: each data column carries a PascalCase `sortField`.
  const handleSort = (column, sortDirection) => {
    if (!column?.sortField) return;
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      name: headerLabel('Document Name'),
      selector: row => row.documentName,
      sortable: true,
      sortField: SORT_FIELDS.documentName,
      wrap: true,
      grow: 2,
      cell: row => (
        <div className='d-flex flex-column py-1'>
          <span>{row.documentName}</span>
          {row.fileName && (
            <small className='text-muted'>
              <i className='ri-attachment-2 me-1'></i>
              {row.fileName}
              {row.fileSize ? ` (${formatFileSize(row.fileSize)})` : ''}
            </small>
          )}
        </div>
      ),
    },
    {
      name: headerLabel('Description'),
      selector: row => row.description,
      sortable: true,
      sortField: SORT_FIELDS.description,
      wrap: true,
      grow: 3,
    },
    {
      name: headerLabel('Document Type'),
      selector: row => row.documentType,
      sortable: true,
      sortField: SORT_FIELDS.documentType,
      width: '160px',
      cell: row => (
        <span
          className={`badge ${
            row.documentType === 'Procurement' ? 'bg-primary' : 'bg-success'
          }`}
        >
          {row.documentType}
        </span>
      ),
    },
    {
      name: headerLabel('Action'),
      width: '140px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-success btn-sm btn-icon'
            onClick={() => handleView(row)}
            disabled={previewLoadingId === row.id}
            title='Preview / Download'
          >
            {previewLoadingId === row.id ? (
              <span
                className='spinner-border spinner-border-sm'
                role='status'
              />
            ) : (
              <i className='ri-eye-fill align-bottom'></i>
            )}
          </button>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row)}
            title='Edit'
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row)}
            title='Delete'
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Document Upload' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='DocumentUploadList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Document Upload
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <Input
                          type='select'
                          className='form-control form-control-sm'
                          style={{ width: 'auto' }}
                          value={documentTypeFilter}
                          onChange={e => {
                            setDocumentTypeFilter(e.target.value);
                            setPage(1);
                          }}
                        >
                          <option value='all'>All Document Types</option>
                          {documentTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={handleNewModal}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Create Document
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No document found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addDocumentUploadModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null ? 'Edit Document' : 'Create Document'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='documentName' className='form-label'>
                          Document Name <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='documentName'
                          name='documentName'
                          className='form-control'
                          maxLength={250}
                          invalid={!!errors.documentName}
                          value={formData.documentName || ''}
                          onChange={handleChange}
                        />
                        {errors.documentName && (
                          <div className='invalid-feedback d-block'>
                            {errors.documentName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='description' className='form-label'>
                          Description <span className='text-danger'>*</span>
                        </Label>
                        <textarea
                          id='description'
                          name='description'
                          className={`form-control ${
                            errors.description ? 'is-invalid' : ''
                          }`}
                          rows='3'
                          value={formData.description || ''}
                          onChange={handleChange}
                        ></textarea>
                        {errors.description && (
                          <div className='invalid-feedback d-block'>
                            {errors.description}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='documentType' className='form-label'>
                          Document Type <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='documentType'
                          name='documentType'
                          className='form-control'
                          invalid={!!errors.documentType}
                          value={formData.documentType || 'Procurement'}
                          onChange={handleChange}
                        >
                          {documentTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                        {errors.documentType && (
                          <div className='invalid-feedback d-block'>
                            {errors.documentType}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='fileUpload' className='form-label'>
                          File{' '}
                          {formData.id === null && (
                            <span className='text-danger'>*</span>
                          )}
                        </Label>
                        {formData.id !== null && formData.fileName ? (
                          <div className='d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light'>
                            <i className='ri-file-text-line fs-16'></i>
                            <span className='text-truncate flex-grow-1 small'>
                              {formData.fileName}
                            </span>
                          </div>
                        ) : null}
                        <Input
                          type='file'
                          id='fileUpload'
                          name='fileUpload'
                          innerRef={fileInputRef}
                          invalid={!!errors.file}
                          onChange={handleFileChange}
                        />
                        {errors.file && (
                          <div className='invalid-feedback d-block'>
                            {errors.file}
                          </div>
                        )}
                        {formData.id !== null && (
                          <small className='text-muted d-block mt-1'>
                            Leave empty to keep the current file.
                          </small>
                        )}
                        {selectedFile && (
                          <small className='text-muted d-block mt-1'>
                            <i className='ri-file-text-line me-1'></i>
                            {selectedFile.name} (
                            {formatFileSize(selectedFile.size)})
                          </small>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
                disabled={saving}
              >
                Close
              </Button>
              <Button
                color='primary'
                className='btn-sm'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>

          <Modal
            isOpen={previewModal}
            role='dialog'
            centered
            size='xl'
            toggle={closePreview}
          >
            <ModalHeader className='bg-soft-info' toggle={closePreview}>
              <span className='modal-title text-info fw-bold'>
                {previewRow?.documentName || 'Preview'}
              </span>
            </ModalHeader>
            <ModalBody style={{ minHeight: '60vh' }}>
              {previewRow &&
                (previewRow.fileContentType?.includes('pdf') ? (
                  <iframe
                    title='document-preview'
                    src={previewUrl}
                    style={{ width: '100%', height: '75vh', border: 'none' }}
                  />
                ) : previewRow.fileContentType?.startsWith('image/') ? (
                  <div className='text-center'>
                    <img
                      src={previewUrl}
                      alt={previewRow.fileName || 'preview'}
                      style={{ maxWidth: '100%', maxHeight: '75vh' }}
                    />
                  </div>
                ) : (
                  <div className='text-center py-5'>
                    <i
                      className='ri-file-text-line'
                      style={{ fontSize: 48, color: '#6c757d' }}
                    ></i>
                    <p className='mt-2 mb-1'>{previewRow.fileName || 'File'}</p>
                    <p className='text-muted'>
                      Tipe file ini tidak bisa dipratinjau inline. Silakan
                      unduh.
                    </p>
                  </div>
                ))}
            </ModalBody>
            <ModalFooter>
              <Button color='light' className='btn-sm' onClick={closePreview}>
                Close
              </Button>
              <Button
                color='primary'
                className='btn-sm'
                onClick={handleDownloadPreview}
              >
                <i className='ri-download-2-line align-bottom me-1'></i>
                Download
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DocumentUploadView;
